import { useEffect, Children } from 'react';
import { useNode, useEditor, Element } from '@craftjs/core';
import { cn } from '../../../../lib/utils';

// Layout presets: key → array of column spans (out of 12)
export const LAYOUT_PRESETS: Record<string, number[]> = {
  '2-equal': [6, 6],
  '3-equal': [4, 4, 4],
  '4-equal': [3, 3, 3, 3],
  '2-wide-left': [8, 4],
  '2-wide-right': [4, 8],
  '3-wide-center': [3, 6, 3],
};

const MAX_COLUMNS = 4;

// ── ColumnContainer ──────────────────────────────────────────────────────────

interface ColumnContainerProps {
  span: number;
  children?: React.ReactNode;
}

export function ColumnContainer({ span, children }: ColumnContainerProps) {
  const {
    connectors: { connect },
  } = useNode();

  if (span <= 0) {
    return <div ref={(ref) => { if (ref) connect(ref); }} style={{ display: 'none' }} />;
  }

  const isEmpty = Children.count(children) === 0;

  return (
    <div
      ref={(ref) => { if (ref) connect(ref); }}
      style={{ gridColumn: `span ${span}` }}
      className="min-h-[60px] rounded border border-dashed border-muted-foreground/30 p-2 transition-colors"
    >
      {children}
      {isEmpty && (
        <div className="flex items-center justify-center h-full text-xs text-muted-foreground/50 select-none pointer-events-none">
          Arraste blocos aqui
        </div>
      )}
    </div>
  );
}

ColumnContainer.craft = {
  displayName: 'Coluna',
  props: {
    span: 6,
  },
  rules: {
    canDrag: () => false,
  },
};

// ── ColumnsBlock ─────────────────────────────────────────────────────────────

interface ColumnsBlockProps {
  layout: string;
  gap: number;
}

export function ColumnsBlock({ layout, gap }: ColumnsBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    linkedNodes,
  } = useNode((state) => ({
    selected: state.events.selected,
    linkedNodes: state.data.linkedNodes,
  }));

  const { actions: editorActions } = useEditor();

  const spans = LAYOUT_PRESETS[layout] ?? LAYOUT_PRESETS['2-equal'];

  // Sync column spans when layout changes
  useEffect(() => {
    if (!linkedNodes) return;
    const currentSpans = LAYOUT_PRESETS[layout] ?? LAYOUT_PRESETS['2-equal'];
    for (let i = 0; i < MAX_COLUMNS; i++) {
      const nodeId = linkedNodes[`col-${i}`];
      if (nodeId) {
        editorActions.setProp(nodeId, (props: ColumnContainerProps) => {
          props.span = currentSpans[i] ?? 0;
        });
      }
    }
  }, [layout, linkedNodes, editorActions]);

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={cn(
        'w-full rounded transition-all',
        selected && 'ring-2 ring-primary ring-offset-2',
      )}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: `${gap}px`,
      }}
    >
      {Array.from({ length: MAX_COLUMNS }, (_, i) => (
        <Element
          key={`col-${i}`}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore — canvas is a Craft.js-specific prop
          canvas
          is={ColumnContainer}
          id={`col-${i}`}
          span={spans[i] ?? 0}
        />
      ))}
    </div>
  );
}

ColumnsBlock.craft = {
  displayName: 'Colunas',
  props: {
    layout: '2-equal',
    gap: 16,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};
